/** @jsx jsx */
import { jsx } from '@emotion/react';
import { daysFromNow } from '@zapier/date';
import { Alert, Link } from '@zapier/design-system';
import { Price } from '@zapier/currency';
import { UserInteractionEvent } from '../../../../universal-nav/utils/avroEvents';
import type { ResponseData } from '../useAlertBanner/useAlertBannerData';
import { BannerProps } from './types';
import { Fragment } from 'react';

import { getProfessionalPlanData } from './utils';

export const shouldRender = (data: ResponseData, isExcludedUrl = false) => {
  const { isTrialing, trialEndDate } = data.currentAccount;
  return !!isTrialing && !!trialEndDate && !isExcludedUrl;
};

export const Component = function ProfessionalTrialing(props: BannerProps) {
  const { data, currency } = props;
  const { currentAccount, possiblePlans } = data;

  const { trialEndDate } = currentAccount;
  const trialDaysRemaining = daysFromNow(trialEndDate!);
  const daysText = trialDaysRemaining === 1 ? 'day' : 'days';

  const { professionalUpgradeUrl, professionalPlanAmount } =
    getProfessionalPlanData(possiblePlans);

  const upgradeUrl = professionalUpgradeUrl;
  const nextPlanAmount = professionalPlanAmount;

  const onClickLink = ({ interactionGoal }: { interactionGoal: string }) => {
    props.trackEvent(UserInteractionEvent, {
      interaction_name: 'professional_trial_banner_cta_clicked',
      interaction_goal: interactionGoal,
      event_action: 'click',
      option_selected: 'not_applicable',
    });
  };

  return (
    <Alert
      layout={props.layout}
      title="Welcome to your Professional trial!"
      type="info"
    >
      <p data-zds>
        {`You have ${trialDaysRemaining} ${daysText} to try Zapier’s `}
        <Link
          href="https://help.zapier.com/hc/en-us/articles/8496197192461-Get-started-with-your-free-Zapier-trial"
          onClick={() => onClickLink({ interactionGoal: 'learn_more' })}
        >
          paid features
        </Link>
        .{' '}
        <Fragment>
          <Link
            href={upgradeUrl}
            onClick={() => onClickLink({ interactionGoal: 'upgrade_plan' })}
          >
            Upgrade
          </Link>{' '}
          anytime
          {nextPlanAmount > 0 && (
            <span data-zds>
              {' '}
              for as low as <Price currency={currency}>{nextPlanAmount}</Price>
              /month.
            </span>
          )}
        </Fragment>
      </p>
    </Alert>
  );
};
